import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '@lf/analytics-events';
import AnimalCard from '../../components/AnimalCard';
import FeedbackButton from '../../components/FeedbackButton';
import { LoadingIndicator } from '@lf/ui-primitives';
import SaveHeartButton from '../../components/SaveHeartButton';
import { useError } from '../../contexts/ErrorContext';
import { getAnimal } from '../../lib/videos/getAnimal';
import { parseSearchParam } from '../../lib/videos/parseSearchParam';
import { useSavedVideo } from '../../lib/videos/useSavedVideo';
import { useStarRewards } from '../../lib/videos/useStarRewards';
import { useVideoActions } from '../../lib/videos/useVideoActions';
import { useVideoPlaybackPersistence } from '../../lib/videos/useVideoPlaybackPersistence';
import { useVideoProgress } from '../../lib/videos/useVideoProgress';
import { useVideoQnaState } from '../../lib/videos/useVideoQnaState';
import CelebrationConfetti from './CelebrationConfetti';
import LiteVideoView from './LiteVideoView';
import PrimaryActionBar from './PrimaryActionBar';
import QuizPanel from './QuizPanel';
import TakeawayTimeline from './TakeawayTimeline';
import VideoChat from './VideoChat';
import VideoPlayer from './VideoPlayer';
import VideoProgressBar from './VideoProgressBar';

const VIDEO_SOURCES = new Set([
  'study_plan', 'rec_path', 'search', 'saved',
  'all_videos', 'home', 'direct',
]);

function buildVideoEventParams(videoId) {
  const { studyPlanId, lessonId, source, searchQueryId, recPosition } = parseSearchParam();
  const resolvedSource = VIDEO_SOURCES.has(source) ? source : 'direct';
  const params = {
    video_id: videoId,
    source: resolvedSource,
  };
  if (lessonId) params.lesson_id = lessonId;
  if (studyPlanId) params.study_plan_id = studyPlanId;
  if (resolvedSource === 'search' && searchQueryId) {
    params.search_query_id = searchQueryId;
  }
  if (resolvedSource === 'rec_path' && recPosition !== null && recPosition !== undefined) {
    const parsed = parseInt(recPosition, 10);
    if (!Number.isNaN(parsed)) params.rec_position = parsed;
  }
  return params;
}

function buttonLabelFor(state, starCount) {
  switch (state) {
    case 'LOADING':
    case 'LOADING_QNA':
      return 'Loading...';
    case 'TAKEAWAYS_HIDDEN':
    case 'FIRST_QNA':
      return 'Take quiz';
    case 'TAKEAWAYS_SHOWN':
      return 'Loading quiz...';
    case 'QNA':
      return starCount > 0 ? `Claim ${starCount} stars` : 'Next question';
    case 'LAST_QNA':
    case 'NO_QNA':
      return 'Done';
    default:
      return 'Unknown';
  }
}

const VideoDetail = ({
  profile,
  user,
  videoId,
  channelId,
  videoMeta,
  hasCompletedVideos,
  disableInteractiveFeatures = false,
}) => {
  const { setError } = useError();
  const navigate = useNavigate();

  const progress = useVideoProgress();
  const qnaState = useVideoQnaState({
    videoId, channelId, user, disableInteractiveFeatures, setError,
  });
  const stars = useStarRewards({ videoId });
  const saved = useSavedVideo({ videoId, channelId, videoMeta, setError });

  const [bottomVisible, setBottomVisible] = useState(false);
  const bottomRef = useRef(null);
  // Track which progress quartile (25/50/75/100) has fired per videoId
  // so video_progress / video_completed don't double-fire while the
  // YouTube player polls every 500ms.
  const firedProgressRef = useRef({ videoId: null, played: false, q25: false, q50: false, q75: false, q100: false });

  const { onNext, onSelectStep, onAnswerEoc } = useVideoActions({
    videoId, channelId, qnaState, stars, setError, setBottomVisible, navigate,
  });

  useVideoPlaybackPersistence({
    videoId,
    channelId,
    videoProgress: progress.videoProgress,
    setError,
  });

  // Reset auxiliary UI state when the video changes.
  useEffect(() => {
    if (!videoId) return;
    progress.reset();
    setBottomVisible(false);
    firedProgressRef.current = { videoId, played: false, q25: false, q50: false, q75: false, q100: false };
    // progress is a stable handle of callbacks; depend only on videoId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Fire `video_played` once per videoId once duration is known (the
  // YT iframe API only reports duration after the player is ready).
  useEffect(() => {
    if (!videoId || disableInteractiveFeatures) return;
    const ref = firedProgressRef.current;
    if (ref.videoId !== videoId || ref.played) return;
    if (progress.duration > 0) {
      ref.played = true;
      track('video_played', {
        ...buildVideoEventParams(videoId),
        position_pct: 0,
      });
    }
  }, [videoId, progress.duration, disableInteractiveFeatures]);

  // Fire `video_progress` at 25/50/75 (throttled, once per quartile)
  // and `video_completed` at 100 once the YT player crosses each
  // threshold. The 500ms polling cadence in VideoPlayer means this
  // useEffect runs frequently; the firedProgressRef guards prevent
  // duplicates.
  useEffect(() => {
    if (!videoId || disableInteractiveFeatures) return;
    const ref = firedProgressRef.current;
    if (ref.videoId !== videoId) return;
    const pct = progress.videoProgress;
    if (!pct || pct <= 0) return;

    if (pct >= 25 && !ref.q25) {
      ref.q25 = true;
      track('video_progress', { ...buildVideoEventParams(videoId), position_pct: 25 });
    }
    if (pct >= 50 && !ref.q50) {
      ref.q50 = true;
      track('video_progress', { ...buildVideoEventParams(videoId), position_pct: 50 });
    }
    if (pct >= 75 && !ref.q75) {
      ref.q75 = true;
      track('video_progress', { ...buildVideoEventParams(videoId), position_pct: 75 });
    }
    if (pct >= 100 && !ref.q100) {
      ref.q100 = true;
      track('video_completed', { ...buildVideoEventParams(videoId), position_pct: 100 });
    }
  }, [videoId, progress.videoProgress, disableInteractiveFeatures]);

  useEffect(() => {
    if (bottomVisible && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [bottomVisible]);

  if (!videoId) {
    return <div className="mt-12"><LoadingIndicator /></div>;
  }

  if (disableInteractiveFeatures) {
    return (
      <LiteVideoView
        videoId={videoId}
        channelId={channelId}
        progress={progress}
        saved={saved}
      />
    );
  }

  const state = qnaState.currentState();
  const buttonLabel = buttonLabelFor(state, stars.starCount);
  const showTimeline =
    qnaState.selectedComponentIndex === -1
    && qnaState.timedTakeaways.length > 0
    && progress.duration > 0
    && state !== 'LOADING';

  const showPrimaryButton =
    ['LOADING', 'LOADING_QNA', 'TAKEAWAYS_HIDDEN', 'NO_QNA'].includes(state)
    || (state === 'FIRST_QNA' && qnaState.selectedComponentIndex < 1)
    || (state === 'LAST_QNA' && qnaState.videoDetail.qna.length === 0);

  const showQuizPanel =
    qnaState.selectedComponentIndex >= 1
    && ['TAKEAWAYS_SHOWN', 'QNA', 'LAST_QNA', 'FIRST_QNA',
        'TAKEAWAYS_HIDDEN', 'NO_QNA'].includes(state);

  return (
    <div className="flex flex-col text-left">
      <VideoPlayer
        disableInteractiveFeatures={disableInteractiveFeatures}
        feedbackContentIds={[videoId, channelId]}
        onProgress={progress.handleProgressUpdate}
        onReady={progress.handlePlayerReady}
        onTiming={progress.handleTimingUpdate}
        showFeedbackButton={false}
        videoId={videoId}
      />
      {qnaState.selectedComponentIndex === -1 && state !== 'LOADING' && (
        showTimeline ? (
          <div className="px-2 pt-2 pb-4">
            <TakeawayTimeline
              duration={progress.duration}
              currentTime={progress.currentTime}
              takeaways={qnaState.timedTakeaways}
              onSeek={progress.handleSeek}
            />
          </div>
        ) : (
          <VideoProgressBar progress={progress.videoProgress} />
        )
      )}
      {qnaState.selectedComponentIndex === -1 && (
        <div className="flex justify-end gap-2 mt-2 px-2">
          <SaveHeartButton
            isSaved={saved.isSaved}
            onClick={saved.handleSaveToggle}
            disabled={saved.isSaving}
          />
          <FeedbackButton contentIds={[videoId, channelId]} />
        </div>
      )}
      <div className="p-2 w-full md:w-4/5 lg:w-3/5">
        {qnaState.isAskingQuestion
          && qnaState.selectedComponentIndex === -1
          && qnaState.videoDetail.question?.messages && (
            <div className="mb-6">
              <AnimalCard animal={getAnimal(videoId)}>
                <VideoChat
                  key={qnaState.videoDetail.question.chat_id}
                  channelId={channelId}
                  question={qnaState.videoDetail.question}
                />
              </AnimalCard>
            </div>
          )}

        {showPrimaryButton && (
          <PrimaryActionBar
            state={state}
            buttonLabel={buttonLabel}
            onNext={onNext}
            isAskingQuestion={qnaState.isAskingQuestion}
            onAskQuestion={() => {
              const question = qnaState.videoDetail?.question;
              if (question?.chat_id) {
                const { studyPlanId, lessonId } = parseSearchParam();
                track('qna_started', {
                  video_id: videoId,
                  chat_id: question.chat_id,
                  ...(lessonId ? { lesson_id: lessonId } : {}),
                  ...(studyPlanId ? { study_plan_id: studyPlanId } : {}),
                });
              }
              qnaState.setIsAskingQuestion(true);
            }}
            hasCompletedVideos={hasCompletedVideos}
          />
        )}

        {showQuizPanel && (
          <QuizPanel
            videoId={videoId}
            qna={qnaState.videoDetail.qna}
            selectedComponentIndex={qnaState.selectedComponentIndex}
            totalStarCount={stars.totalStarCount}
            displayedStarCount={stars.displayedStarCount}
            starCount={stars.starCount}
            state={state}
            buttonLabel={buttonLabel}
            onSelectStep={onSelectStep}
            onAnswerEoc={onAnswerEoc}
            onNext={onNext}
            eoc={qnaState.eoc}
          />
        )}
      </div>
      <div ref={bottomRef}></div>
      {stars.showConfetti && <CelebrationConfetti />}
    </div>
  );
};

export default VideoDetail;
